import { skipToken } from '@reduxjs/toolkit/query/react';
import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'store';
import { Avatar, Tooltip } from '@mui/material';
import { IconBell } from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import {
  KnockProvider,
  KnockFeedProvider,
  NotificationFeedPopover,
  NotificationFeed,
  NotificationFeedContainer,
} from '@knocklabs/react';

// Required CSS import, unless you're overriding the styling
import '@knocklabs/react/dist/index.css';
import 'assets/scss/knock-theme.css';

import {
  useGetUserNotificationAuthQuery,
  useGetUserQuery,
} from 'store/slices/apiV1/user';
import { KnockUnreadBadge } from 'ui-component/third-party/KnockUnreadBadge';

import { roundedButtonAvatarSx } from 'constants/themeConstants';
import { createSxWithoutUndefined } from 'utils/functions';
import _ from 'lodash';

interface KnockNotificationWidgetProps {
  authWithKnock: boolean;
  variant?: 'icon' | 'inbox';
}

const KnockNotificationWidget = ({
  authWithKnock,
  variant = 'icon',
}: KnockNotificationWidgetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const notifButtonRef = useRef(null);
  const theme = useTheme();
  const navigate = useNavigate();

  const { activeOrgId } = useSelector((state) => state.org);
  const { isLoggedIn } = useSelector(({ session }) => session);

  const [stopLoop, setStopLoop] = useState<boolean>(false);
  const testKnockAuthUrl = (knock_user_id: string) =>
    `https://api.knock.app/v1/users/${knock_user_id}/feeds/${process.env.KNOCK_FEED_ID}`;

  const { data: userData } = useGetUserQuery(
    isLoggedIn ? undefined : skipToken
  );

  const { data: knockAuth, refetch: refetchKnockAuth } =
    useGetUserNotificationAuthQuery(isLoggedIn ? undefined : skipToken);
  const knockToken = knockAuth?.token;
  const knockTokenExpiration = knockAuth?.expires;

  useEffect(() => {
    if (authWithKnock) {
      if (
        !knockTokenExpiration ||
        new Date(knockTokenExpiration) < new Date()
      ) {
        refetchKnockAuth();
      }
    }
  }, [knockTokenExpiration, refetchKnockAuth]);

  useEffect(() => {
    if (!authWithKnock || !knockToken || !userData) return;
    // Test if the token we got is valid on Knock's side.
    const checkCredsWithKnock = async () => {
      const knockHeaders = new Headers();
      knockHeaders.append('x-knock-user-token', knockToken);
      knockHeaders.append(
        'Authorization',
        `Bearer ${process.env.KNOCK_API_KEY}`
      );
      const response = await fetch(
        testKnockAuthUrl(`${userData.id}-${activeOrgId}`),
        {
          headers: knockHeaders,
        }
      );
      const data = await response.json();
      if (data.status && data.status === 403 && !stopLoop) {
        // refetch it because the token is bad
        refetchKnockAuth();
        setStopLoop(true);
      }
    };
    checkCredsWithKnock();
  }, [knockToken, userData, activeOrgId, stopLoop, refetchKnockAuth]);

  // Early returns for missing configuration pieces
  if (!userData) return null;
  if (!process.env.KNOCK_API_KEY) return null;
  if (!process.env.KNOCK_FEED_ID) return null;

  // Knock does not require a JWT in development so we do not pass one
  if (!authWithKnock)
    return (
      <KnockProvider
        apiKey={process.env.KNOCK_API_KEY}
        userId={`${userData.id}-${activeOrgId}`}
      >
        <KnockFeedProvider
          feedId={process.env.KNOCK_FEED_ID}
          defaultFeedOptions={{ tenant: activeOrgId || undefined }}
        >
          <NotificationFeedContainer>
            {variant === 'inbox' ? (
              <NotificationFeed />
            ) : (
              <>
                <KnockUnreadBadge
                  badgeCountType="unread"
                  content={
                    <Tooltip title="Notification Inbox">
                      <Avatar
                        variant="rounded"
                        sx={createSxWithoutUndefined(
                          roundedButtonAvatarSx(theme)
                        )}
                        onClick={(__) => setIsOpen((prevIsOpen) => !prevIsOpen)}
                        color="inherit"
                        ref={notifButtonRef}
                      >
                        <IconBell stroke={1.5} size="1.3rem" />
                      </Avatar>
                    </Tooltip>
                  }
                />
                <NotificationFeedPopover
                  buttonRef={notifButtonRef}
                  isVisible={isOpen}
                  onClose={() => setIsOpen(false)}
                />
              </>
            )}
          </NotificationFeedContainer>
        </KnockFeedProvider>
      </KnockProvider>
    );
  // Otherwise we are in prod and would wait until the token is set to render
  if (!knockToken) return null;
  return (
    <KnockProvider
      apiKey={process.env.KNOCK_API_KEY}
      userId={`${userData.id}-${activeOrgId}`}
      userToken={knockToken}
    >
      <KnockFeedProvider
        feedId={process.env.KNOCK_FEED_ID}
        defaultFeedOptions={{ tenant: activeOrgId || undefined }}
      >
        <NotificationFeedContainer>
          {variant === 'inbox' ? (
            <NotificationFeed />
          ) : (
            <>
              <KnockUnreadBadge
                badgeCountType="unread"
                content={
                  <Avatar
                    variant="rounded"
                    sx={createSxWithoutUndefined({
                      ...theme.typography.commonAvatar,
                      ...theme.typography.mediumAvatar,
                      overflow: 'hidden',
                      transition: 'all .2s ease-in-out',
                      background: theme.palette.brand.moss,
                      color: theme.palette.brand.steel,
                      '&:hover': {
                        background: theme.palette.primary.dark,
                        color: theme.palette.common.white,
                      },
                    })}
                    onClick={(__) => setIsOpen((prevIsOpen) => !prevIsOpen)}
                    color="inherit"
                    ref={notifButtonRef}
                  >
                    <IconBell stroke={1.5} size="1.3rem" />
                  </Avatar>
                }
              />
              <NotificationFeedPopover
                buttonRef={notifButtonRef}
                isVisible={isOpen}
                onClose={() => setIsOpen(false)}
                onNotificationClick={(item) => {
                  navigate(
                    _.get(item, ['blocks', 1, 'rendered']).replace(
                      'https://platform.cofactr.com',
                      ''
                    )
                  );
                }}
              />
            </>
          )}
        </NotificationFeedContainer>
      </KnockFeedProvider>
    </KnockProvider>
  );
};

export default KnockNotificationWidget;
